import React, { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, Space, Table } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { useLoadList } from "../../../hooks";
import { useAuth } from "../../../hooks/useAuth";
import { scheduleService } from "../../../api/enhancedApi";
import { formatDate, formatDateTime } from "../../../utils/dateUtils";
import AircraftTag from "../../../components/AircraftTag";
import AirlineIngestReviewModal from "./AirlineIngestReviewModal";
import AirlineIngestSourceButtons from "./AirlineIngestSourceButtons";
import AirlineIngestUploadButton from "./AirlineIngestUploadButton";
import AirlineIngestWorkspacePanel from "./AirlineIngestWorkspacePanel";
import { useAirlineScheduleIngest } from "./useAirlineScheduleIngest";
import { hasIngestSource, resetOriginListPagination } from "./ingestListPresentation";
import { getStatusColor, getStatusText } from "../OfficialList/utils/scheduleUtils";

export default function AirlineIngestOfficialWorkspace({
    showPageTitle = true,
    onOpenSchedule,
    onIngestConfirmed,
    reloadToken = 0,
}) {
    const { hasPermission } = useAuth();
    const canWrite = hasPermission("airline_schedule_ingest:write");
    const ingest = useAirlineScheduleIngest({ enabled: true });
    const [reviewTaskId, setReviewTaskId] = useState(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
    });
    const { data, isLoading, refetch } = useLoadList("ingestOriginSchedules", scheduleService.getIngestOriginScheduleList, {
        page: pagination.current,
        page_size: pagination.pageSize,
    });

    useEffect(() => {
        if (!reloadToken) {
            return undefined;
        }
        refetch();
        return undefined;
    }, [reloadToken, refetch]);

    const refreshAfterConfirm = useCallback(() => {
        ingest.refetch();
        setPagination((current) => resetOriginListPagination(current));
        refetch({ page: 1, page_size: pagination.pageSize });
        onIngestConfirmed?.();
    }, [ingest, refetch, onIngestConfirmed, pagination.pageSize]);

    const columns = [
        {
            title: "计划名称",
            dataIndex: "name",
            key: "name",
            ellipsis: true,
            render: (text, record) => (
                <a
                    href="#"
                    onClick={(event) => {
                        event.preventDefault();
                        onOpenSchedule?.(record);
                    }}
                >
                    {text || "未命名计划"}
                </a>
            ),
        },
        {
            title: "时间范围",
            key: "date_range",
            width: 180,
            render: (_, record) => {
                if (!record.start_date && !record.end_date) return "无时间范围";
                const start = record.start_date ? formatDate(record.start_date) : "未设置";
                const end = record.end_date ? formatDate(record.end_date) : "未设置";
                return `${start} 至 ${end}`;
            },
        },
        {
            title: "飞机",
            key: "aircraft",
            width: 120,
            render: (_, record) => {
                if (!record.aircraft) return "未分配";
                return <AircraftTag registration={record.aircraft.registration || "未知"} />;
            },
        },
        {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 120,
            render: (status) => <Badge className="status-badge" status={getStatusColor(status)} text={getStatusText(status)} />,
        },
        {
            title: "更新时间",
            dataIndex: "updated_at",
            key: "updated_at",
            width: 180,
            render: (text) => (text ? formatDateTime(text) : "未知"),
        },
        {
            title: "操作",
            key: "action",
            width: 140,
            render: (_, record) => (
                <Space size="small" wrap={false} className="table-actions">
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => onOpenSchedule?.(record)}
                    >
                        详情
                    </Button>
                    {hasIngestSource(record) ? <AirlineIngestSourceButtons variant="menu" scheduleId={record.id} /> : null}
                </Space>
            ),
        },
    ];

    return (
        <>
            <div className="page-header-compact">
                {showPageTitle ? <div className="page-title-compact">航司班期接入</div> : <div className="page-title-compact"> </div>}
                <div className="page-action-compact">
                    {canWrite ? (
                        <AirlineIngestUploadButton onFile={(file) => ingest.startFromFile(file)}>航司班期接入</AirlineIngestUploadButton>
                    ) : null}
                </div>
            </div>
            <AirlineIngestWorkspacePanel
                tasks={ingest.tasks}
                onReview={setReviewTaskId}
                onRetry={ingest.retryTask}
                onDiscard={ingest.discardTask}
            />
            <Card className="data-card" title="由接入写成的计划">
                <Table
                    size="small"
                    columns={columns}
                    dataSource={data?.items || []}
                    rowKey="id"
                    loading={isLoading}
                    pagination={{
                        ...pagination,
                        total: data?.total || 0,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                    }}
                    onChange={(nextPagination) => setPagination(nextPagination)}
                    scroll={{ x: 920 }}
                />
            </Card>
            <AirlineIngestReviewModal
                open={Boolean(reviewTaskId)}
                taskId={reviewTaskId}
                canWrite={canWrite}
                onClose={() => setReviewTaskId(null)}
                onReplaceFile={(file, taskId) =>
                    ingest.startFromFile(file, { replaceTaskId: taskId }).then((task) => {
                        if (task) setReviewTaskId(null);
                    })
                }
                onConfirmed={refreshAfterConfirm}
            />
        </>
    );
}
